import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Alert,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Sms as SmsIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';

interface SmsConsentModalProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  phoneNumber: string;
}

export function formatPhoneNumber(phoneNumber: string) {
  const digits = (phoneNumber || '').replace(/\D/g, '');
  if (digits.length === 10) {
    const area = digits.slice(0, 3);
    const prefix = digits.slice(3, 6);
    const line = digits.slice(6);
    return `+1 ${area} ${prefix}-${line}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    const area = digits.slice(1, 4);
    const prefix = digits.slice(4, 7);
    const line = digits.slice(7);
    return `+1 ${area} ${prefix}-${line}`;
  }
  return phoneNumber;
}

export default function SmsConsentModal({ open, onClose, onAccept, phoneNumber }: SmsConsentModalProps) {
  const [consentCheckboxes, setConsentCheckboxes] = useState({
    understand: false,
    charges: false,
    optOut: false
  });

  const handleCheckboxChange = (field: keyof typeof consentCheckboxes) => {
    setConsentCheckboxes({
      ...consentCheckboxes,
      [field]: !consentCheckboxes[field]
    });
  };

  const handleAccept = () => {
    onAccept();
    // Reset checkboxes after accepting
    setConsentCheckboxes({
      understand: false,
      charges: false,
      optOut: false
    });
  };

  const handleDecline = () => {
    setConsentCheckboxes({
      understand: false,
      charges: false,
      optOut: false
    });
    onClose();
  };

  const allCheckboxesChecked = consentCheckboxes.understand && consentCheckboxes.charges && consentCheckboxes.optOut;

  return (
    <Dialog open={open} onClose={handleDecline} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.light' }}>
            <SmsIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              SMS Notifications Consent
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Please review and accept the following terms
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          By enabling SMS notifications, you agree to receive text messages about your work schedule and shift updates.
        </Alert>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Important Information:
          </Typography>
          <Typography variant="body2" paragraph>
            • Message and data rates may apply based on your carrier's plan
          </Typography>
          <Typography variant="body2" paragraph>
            • Message frequency varies based on your schedule activity
          </Typography>
          <Typography variant="body2">
            • You can opt out at any time by toggling off SMS notifications in your settings or by replying STOP to any message
          </Typography>
        </Alert>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Please confirm you understand:
        </Typography>

        <FormGroup sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={consentCheckboxes.understand}
                onChange={() => handleCheckboxChange('understand')}
              />
            }
            label={
              <Typography variant="body2">
                I understand I will receive SMS notifications about my work schedule to the phone number on file: <strong>{formatPhoneNumber(phoneNumber)}</strong>
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={consentCheckboxes.charges}
                onChange={() => handleCheckboxChange('charges')}
              />
            }
            label={
              <Typography variant="body2">
                I acknowledge that standard message and data rates may apply according to my mobile carrier's plan
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={consentCheckboxes.optOut}
                onChange={() => handleCheckboxChange('optOut')}
              />
            }
            label={
              <Typography variant="body2">
                I understand I can opt out at any time by disabling SMS notifications in settings or replying STOP to any message
              </Typography>
            }
          />
        </FormGroup>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 0 }}>
        <Button onClick={handleDecline} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          disabled={!allCheckboxesChecked}
          startIcon={<CheckCircleIcon />}
        >
          Accept & Enable SMS
        </Button>
      </DialogActions>
    </Dialog>
  );
}